import { useCallback, useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import {
    getQuestionnaires,
    getSubjectAnswers,
} from "../../graphql/queries";
import { upsertSubjectAnswer } from "../../graphql/mutations";
import { v4 as uuidv4 } from "uuid";
import { questionnaireMachine } from "../QuestionnaireStateMachine/machine";

const client = generateClient();

export default function useQuestionnaires({ subjectId }) {
    const [questionnaires, setQuestionnaires] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeQuestionnaires, setActiveQuestionnaires] = useState([]);
    const [collapsedMap, setCollapsedMap] = useState({});

    const loadSubjectQuestionnaires = useCallback(async (isFirstTime = true) => {
        setLoading(true);

        // 1️⃣ Load subject answers
        const answersRes = await client.graphql({
            query: getSubjectAnswers,
            variables: {
                subject_id: [subjectId],
            },
        });

        const answers = answersRes.data.getSubjectAnswers.subject_answers;

        // 2️⃣ Group by questionnaire
        const grouped = answers.reduce((acc, a) => {
            if (!a.questionnaire_id) return acc;
            acc[a.questionnaire_id] ||= {};
            acc[a.questionnaire_id][a.source] ||= {
                answers: [],
                firstAnsweredAt: a.updated_at,
            };

            acc[a.questionnaire_id][a.source].answers.push(a);

            // 👇 keep EARLIEST created_at
            if (
                new Date(a.created_at) <
                new Date(acc[a.questionnaire_id][a.source].firstAnsweredAt)
            ) {
                acc[a.questionnaire_id][a.source].firstAnsweredAt = a.created_at;
            }

            return acc;
        }, {});



        const instances = [];

        Object.entries(grouped).forEach(([questionnaireId, sources]) => {
            Object.entries(sources).forEach(([source, data]) => {
                instances.push({
                    questionnaire_id: Number(questionnaireId),
                    source,
                    firstAnsweredAt: data.firstAnsweredAt,
                    answers: data.answers,
                    outcome: data.outcome
                });
            });
        });

        instances.sort(
            (a, b) =>
                new Date(b.firstAnsweredAt) - new Date(a.firstAnsweredAt)
        );


        // 3️⃣ Load questionnaire templates for these answers
        const qRes = await client.graphql({
            query: getQuestionnaires,
            variables: {
                id: [...new Set(instances.map(i => i.questionnaire_id))],
            },
        });

        const templates = qRes.data.getQuestionnaires.questionnaires;

        const templateMap = Object.fromEntries(
            templates.map(q => [q.id, q])
        );

        const collapsedInit = {};

        const _activeQuestionnaires = instances.map(i => {

            const completedAnswer = i.answers.find(a => a.outcome != null);
            const key = `${i.source}`;

            collapsedInit[key] = true;


            let machineState = null;
            let machineResponse = null;
            let outcome = null;
            let outcome_description = null;
            let template = null;
            let questionnaireName = null;

            if (completedAnswer) {
                // 🔒 COMPLETED → TRUST BACKEND ONLY
                outcome = completedAnswer.outcome;
                outcome_description = completedAnswer.outcome_description ?? null;
                questionnaireName = templateMap[i.questionnaire_id]?.name ?? null;

            } else {


                template = templateMap[i.questionnaire_id];
                questionnaireName = template?.name ?? null;

                let state = null;
                let response = null;

                const sortedAnswers = [...i.answers].sort(
                    (a, b) => new Date(a.created_at) - new Date(b.created_at)
                );

                for (const a of sortedAnswers) {
                    if (!state) {
                        state = {
                            current_question_id: a.original_question,
                            answers: {},
                            completed: false
                        };
                    } else {
                        state = {
                            ...state,
                            current_question_id: a.original_question
                        };
                    }
                    const result = questionnaireMachine({
                        questions_json: template.questions_json,
                        settings_json: template.settings_json,
                        state,
                        answer: a.answer
                    });

                    state = result.state;
                    response = result.response;
                }

                // 🔑 THIS IS THE CRITICAL MISSING STEP
                if (state && !state.completed) {
                    const next = questionnaireMachine({
                        questions_json: template.questions_json,
                        settings_json: template.settings_json,
                        state,
                        answer: null
                    });

                    state = next.state;
                    response = next.response;

                }

                machineState = state;
                machineResponse = response;

                console.log('derived machine state and response from answers', { machineState, machineResponse });


            }

            const answeredCount = new Set(
                i.answers.map(a => a.original_question)
            ).size;

            const totalQuestions = template ? JSON.parse(template.questions_json).length : answeredCount;

            return {
                ...template,
                id: i.questionnaire_id,
                name: questionnaireName,
                source: i.source,
                firstAnsweredAt: i.firstAnsweredAt,
                answers: i.answers,

                // machine (only if in-progress)
                machineState,
                response: machineResponse,

                // 🔑 FINAL, IMMUTABLE
                outcome,
                outcome_description,

                // UI helpers
                answeredCount,
                totalQuestions
            };
        });


        const newActiveQuestionnaires = _activeQuestionnaires.filter(Boolean);



        setActiveQuestionnaires(newActiveQuestionnaires);
        isFirstTime && setCollapsedMap(collapsedInit);
        setLoading(false);
    }, [subjectId]);

    useEffect(() => {
        if (subjectId) {
            loadSubjectQuestionnaires();
        }
    }, [subjectId, loadSubjectQuestionnaires]);


    const handleSelectQuestionnaire = async questionnaire => {
        closeModal();

        // NEW attempt → generate source
        const source = getNewSourceUUID();

        // 🔑 INITIAL MACHINE CALL
        const { state, response } = questionnaireMachine({
            questions_json: questionnaire.questions_json,
            settings_json: questionnaire.settings_json,
            state: null,
            answer: null
        });


        const totalQuestions = JSON.parse(questionnaire.questions_json).length;


        // Load empty answers for this attempt
        setActiveQuestionnaires(prev => [
            {
                ...questionnaire,
                source,
                machineState: state,
                response,
                answers: [],
                answeredCount: 0,
                totalQuestions,
                firstAnsweredAt: new Date().toISOString(),
            },
            ...prev, // newest on top
        ]);

        setCollapsedMap(prev => ({
            ...prev,
            [`${source}`]: false, // expanded
        }));
    };

    /* -----------------------------
       Load questionnaire templates
    ----------------------------- */
    const loadQuestionnaires = useCallback(async () => {
        setLoading(true);

        const res = await client.graphql({
            query: getQuestionnaires,
            variables: {
                sorting: [
                    {
                        field: "created_at",
                        order: "desc",
                    },
                ],
            },
        });

        const list = res.data.getQuestionnaires.questionnaires;
        setQuestionnaires(list);
        setLoading(false);

        return list;
    }, []);

    /* -----------------------------
       Save / update answer
    ----------------------------- */
    const saveAnswer = useCallback(
        async ({ questionnaireId, question, answer, source, original_question, outcome, outcome_description }) => {
            await client.graphql({
                query: upsertSubjectAnswer,
                variables: {
                    input: {
                        subject_answers: {
                            subject_id: subjectId,
                            questionnaire_id: questionnaireId,
                            question,
                            answer: String(answer) || '--',
                            source,
                            original_question,
                            original_answer: String(answer) || '--',
                            outcome,
                            outcome_description
                        },
                    },
                },
            });

            await loadSubjectQuestionnaires(false); // 👈 IMPORTANT (see issue 3)
        },
        [subjectId]
    );

    /* -----------------------------
       Modal helpers
    ----------------------------- */
    const openQuestionnaireModal = async () => {
        if (questionnaires.length === 0) {
            await loadQuestionnaires();
        }
        setShowModal(true);
    };

    const toggleCollapse = key => {
        setCollapsedMap(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };


    const closeModal = () => setShowModal(false);

    const getNewSourceUUID = () => {
        const attemptSource = `ui:${uuidv4()}`;
        return attemptSource;
    }

    const handleRemoveQuestionnaire = async (source, answers) => {
        try {

            if (!window.confirm("Remove this questionnaire?")) return;

            await Promise.all(
                answers.map(a =>
                    client.graphql({
                        query: upsertSubjectAnswer,
                        variables: {
                            input: {
                                subject_answers: { id: a.id }
                            },
                            input_delete: {
                                id: a.id,
                                key: "subject_answers",
                            },
                        },
                    })
                )
            );


            setActiveQuestionnaires(prev => prev.filter(q => q.source !== source));
            setCollapsedMap(prev => {
                const newMap = { ...prev };
                delete newMap[source];
                return newMap;
            });
        } catch (error) {
            console.error('Error deleting answer:', error);
        }
    };

    const onAnswer = async ({
        questionnaireId,
        source,
        answer
    }) => {

        setActiveQuestionnaires(prev =>
            prev.map(q => {

                if (q.id !== questionnaireId || q.source !== source) {
                    return q;
                }

                // 1️⃣ Run machine locally
                const { state: newState, response: newResponse } =
                    questionnaireMachine({
                        questions_json: q.questions_json,
                        settings_json: q.settings_json,
                        state: q.machineState,
                        answer
                    });

                // 2️⃣ Persist to backend (async, don't block UI)
                if (newResponse.save) {
                    client.graphql({
                        query: upsertSubjectAnswer,
                        variables: {
                            input: {
                                subject_answers: {
                                    subject_id: subjectId,
                                    questionnaire_id: questionnaireId,
                                    question: newResponse.save.question_label,
                                    answer: String(answer),
                                    source,
                                    original_question: newResponse.save.question_id,
                                    original_answer: String(answer),
                                    outcome: newResponse.save.outcome,
                                    outcome_description:
                                        newResponse.save.outcome_description
                                }
                            }
                        }
                    });
                }

                // 3️⃣ Update local answers list
                const updatedAnswers = [
                    ...q.answers,
                    {
                        id: Date.now(), // temp id for UI
                        question: newResponse.save?.question_label,
                        answer,
                        original_question: newResponse.save?.question_id,
                        outcome: newResponse.save?.outcome ?? null,
                        outcome_description:
                            newResponse.save?.outcome_description ?? null
                    }
                ];

                // 4️⃣ Return updated questionnaire
                return {
                    ...q,
                    answers: updatedAnswers,
                    answeredCount: updatedAnswers.length,
                    machineState: newState,
                    response: newResponse,
                    outcome: newState.completed ? newState.outcome : q.outcome,
                    outcome_description: newState.completed
                        ? newState.outcome_description
                        : q.outcome_description
                };
            })
        );
    };


    return {
        questionnaires,
        loading,
        showModal,
        activeQuestionnaires,
        collapsedMap,
        toggleCollapse,
        setCollapsedMap,
        openQuestionnaireModal,
        closeModal,
        saveAnswer,
        reload: loadSubjectQuestionnaires,
        setActiveQuestionnaires,
        getNewSourceUUID,
        handleSelectQuestionnaire,
        handleRemoveQuestionnaire,
        onAnswer
    };
}
